import { Injector } from '@angular/core';
import { Router } from '@angular/router';
import { FormID } from '@finxone-platform/form-action';
import { AlertHandlerService } from '@finxone-platform/shared/services';
import { AccountTypes, AlertTypes, BaseWidgetProperties } from '@finxone-platform/shared/sys-config-types';
import { Store } from '@ngxs/store';
import { take, tap } from 'rxjs';
import { UpdateAccountName } from '../../../actions/account.action';
import { ClearFormDataActionWithId } from '../../../actions/form-submission.action';
import { UpdateSavingAccountName } from '../../../actions/savings-account.action';
import { WorkflowFormsService } from '../../../services/workflow-forms/workflow-forms.service';
import { AccountState } from '../../../state/account.state';
import { SavingsAccountState } from '../../../state/savings-account.state';
import { redirectToPage } from '../cta-button-actions.utils';

/**
 * Changes the account nickname/alias for the current account
 *
 * Validates the form input, checks for duplicate nicknames, and dispatches
 * an update action to change the account's display name. Handles validation
 * errors by showing appropriate alerts to the user.
 *
 * @throws Will show warning alerts for:
 * - Invalid form data
 * - Empty nickname input
 * - Nickname identical to current alias
 */
export function changeAccountNickname(widgetProperties: BaseWidgetProperties, injector: Injector): void {
  const store = injector.get(Store);
  const alertsService = injector.get(AlertHandlerService);
  const accountId = store.selectSnapshot(AccountState.getCurrentAccountId);
  const currentAccount = store.selectSnapshot(AccountState.getCurrentAccount);

  const workflowFormsService = injector.get(WorkflowFormsService);
  const formData = workflowFormsService.getFormValuesFlattened(FormID.ACCOUNT_NAME_FORM);
  const nickname = (
    formData?.['input-1'] ||
    formData?.['accountNickname'] ||
    formData?.['nickname'] ||
    formData?.['name'] ||
    ''
  ).trim();
  const isFormValid = workflowFormsService.isFormValid(FormID.ACCOUNT_NAME_FORM);

  if (!isFormValid) {
    workflowFormsService.markFormTouched(FormID.ACCOUNT_NAME_FORM);
    return;
  }
  if (!nickname.length) {
    return alertsService.showAlertFn(AlertTypes.WARNING, 'Please enter a nickname');
  }

  if (widgetProperties?.['dataContext'] === AccountTypes.SAVINGS_ACCOUNT) {
    const savingAccountId = store.selectSnapshot(SavingsAccountState.getCurrentAccountId);
    updateSavingAccountName(savingAccountId, nickname, injector, widgetProperties);
  } else {
    if (nickname === currentAccount?.alias) {
      return alertsService.showAlertFn(
        AlertTypes.WARNING,
        'Please choose a different nickname than the current one',
      );
    }
    dispatchUpdateAccountName(accountId, nickname, injector, widgetProperties);
  }
}

function dispatchUpdateAccountName(
  accountId: string,
  nickname: string,
  injector: Injector,
  widgetProperties: BaseWidgetProperties,
): void {
  const store = injector.get(Store);
  store
    .dispatch(new UpdateAccountName(accountId, nickname))
    .pipe(
      take(1),
      tap({
        next: () => {
          performPostUpdateNavigation(injector, widgetProperties);
        },
        error: (error) => {
          console.error('Error dispatching dispatchUpdateAccountName:', error);
          // still redirect to account-credentials, regardless if API call failed, as per requirements
          performPostUpdateNavigation(injector, widgetProperties);
        },
      }),
    )
    .subscribe();
}

function updateSavingAccountName(
  accountId: string,
  nickname: string,
  injector: Injector,
  widgetProperties: BaseWidgetProperties,
): void {
  const store = injector.get(Store);
  store
    .dispatch(new UpdateSavingAccountName(accountId, nickname))
    .pipe(
      take(1),
      tap({
        next: () => {
          performPostUpdateNavigation(injector, widgetProperties);
        },
        error: (error) => {
          console.error('Error dispatching updateSavingAccountName:', error);
          // still redirect to account-credentials, regardless if API call failed, as per requirements
          performPostUpdateNavigation(injector, widgetProperties);
        },
      }),
    )
    .subscribe();
}

const performPostUpdateNavigation = (injector: Injector, widgetProperties: BaseWidgetProperties) => {
  const store = injector.get(Store);
  const router = injector.get(Router);
  redirectToPage(router, widgetProperties, injector);
  store.dispatch(new ClearFormDataActionWithId(FormID.ACCOUNT_NAME_FORM));
};
